/**
 * Payment Chart Component
 * Displays payment amounts over time using bar chart
 *
 * @description
 * A bar chart component showing:
 * - Payment amounts per period (purple bars)
 * - Total paid amount in header
 * - Responsive design
 * - Loading skeleton state
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-3
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PaymentChartResponse } from '@/services/manufacturing/manufacturingApi';

// ============================================
// TYPE DEFINITIONS
// ============================================

interface PaymentChartProps {
  data?: PaymentChartResponse;
  loading?: boolean;
  className?: string;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Format currency value - handles NaN and undefined values
 */
const formatCurrency = (value: number | undefined | null): string => {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(safeValue);
};

// ============================================
// LOADING SKELETON
// ============================================

const PaymentChartSkeleton: React.FC = () => {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32 mt-2" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-[300px] w-full" />
      </CardContent>
    </Card>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const PaymentChart: React.FC<PaymentChartProps> = ({
  data,
  loading = false,
  className
}) => {
  // Show skeleton during loading
  if (loading || !data) {
    return <PaymentChartSkeleton />;
  }

  // Transform data for recharts
  const chartData = data.labels?.map((label, index) => {
    const dataPoint: Record<string, string | number> = { name: label };

    data.datasets?.forEach((dataset) => {
      dataPoint[dataset.label] = dataset.data[index] || 0;
    });

    return dataPoint;
  }) || [];

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Payment Trends</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="name"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
              angle={-35}
              textAnchor="end"
              height={60}
              interval="preserveStartEnd"
            />
            <YAxis
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px'
              }}
              formatter={(value: number) => formatCurrency(value)}
            />
            <Legend />
            {data.datasets?.map((dataset) => (
              <Bar
                key={dataset.label}
                dataKey={dataset.label}
                fill="#a855f7"
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default PaymentChart;
